import { ROBOT_WS_ADDRESS } from "@/constants";
import { robotWsClient } from "@/sockets/robot";
import ArmsRum from "@arms/rum-browser";
import {
  chatSdkHooks,
  globalChannelMessageWsClient,
  GLOBAL_ADDRESS,
  WECHAT_ACCOUNT_WS_ADDRESS,
  WECHAT_CHAT_WS_ADDRESS,
  WECHAT_CONTACT_WS_ADDRESS,
  wechatAccountWsClient,
  wechatChatWsClient,
  wechatContactWsClient,
  WECOM_ACCOUNT_WS_ADDRESS,
  WECOM_CHAT_WS_ADDRESS,
  WECOM_CONTACT_WS_ADDRESS,
  wecomAccountWsClient,
  wecomChatWsClient,
  wecomContactWsClient,
} from "@packages/chat-sdk";
import { Alert, message, notification } from "antd";
import { createRoot } from "react-dom/client";

const container = document.createElement("div");
container.setAttribute("id", "network-status");
const root = createRoot(container);
document.body.appendChild(container);

window.addEventListener("offline", () => {
  root.render(
    <Alert
      message="网络连接已断开，请检查网络配置"
      type="error"
      showIcon
      closable
      style={{
        position: "fixed",
        top: 0,
        left: "50%",
        transform: "translate(-50%, 0)",
        width: "50%",
        zIndex: 1000,
      }}
    />
  );
});
window.addEventListener("online", () => {
  root.render(null);
});

chatSdkHooks.common.sendMessageError.subscribe(() => {
  message.warning("请求失败，请检查网络设置");
});

const wsErrorMessageMap = {
  [WECHAT_ACCOUNT_WS_ADDRESS]:
    "wechat account ws错误:" + WECHAT_ACCOUNT_WS_ADDRESS,
  [WECHAT_CHAT_WS_ADDRESS]: "wechat chat ws错误:" + WECHAT_CHAT_WS_ADDRESS,
  [WECHAT_CONTACT_WS_ADDRESS]:
    "wechat contact ws错误:" + WECHAT_CONTACT_WS_ADDRESS,
  [WECOM_ACCOUNT_WS_ADDRESS]:
    "wecom account ws错误:" + WECOM_ACCOUNT_WS_ADDRESS,
  [WECOM_CHAT_WS_ADDRESS]: "wecom chat ws错误:" + WECOM_CHAT_WS_ADDRESS,
  [WECOM_CONTACT_WS_ADDRESS]:
    "wecom contact ws错误:" + WECOM_CONTACT_WS_ADDRESS,
};
chatSdkHooks.common.wsErrorHook.subscribe((data) => {
  ArmsRum.sendException({
    name: "ws连接异常",
    message: wsErrorMessageMap[data.path],
    properties: {
      error_time: new Date().toLocaleString(),
      connection_duration: Math.round(data.connectionDuration / 1000),
    },
  });

  /** 小于30s视为重连失败，避免小数据过多，不统计进连接时长、避免拉低平均值 */
  if (data.connectionDuration > 30 * 1000) {
    ArmsRum.sendCustom({
      type: "ws异常断开连接时长统计",
      name: wsErrorMessageMap[data.path],
      group: "ws_error",
      value: Math.round(data.connectionDuration / 1000),
      properties: {
        error_time: new Date().toLocaleString(),
      },
    });
  }
});

const autoReconnectionFailMessageMap = {
  [WECHAT_ACCOUNT_WS_ADDRESS]:
    "wechat account ws自动重连失败:" + WECHAT_ACCOUNT_WS_ADDRESS,
  [WECHAT_CHAT_WS_ADDRESS]:
    "wechat chat ws自动重连失败:" + WECHAT_CHAT_WS_ADDRESS,
  [WECHAT_CONTACT_WS_ADDRESS]:
    "wechat contact ws自动重连失败:" + WECHAT_CONTACT_WS_ADDRESS,
  [WECOM_ACCOUNT_WS_ADDRESS]:
    "wecom account ws自动重连失败:" + WECOM_ACCOUNT_WS_ADDRESS,
  [WECOM_CHAT_WS_ADDRESS]: "wecom chat ws自动重连失败:" + WECOM_CHAT_WS_ADDRESS,
  [WECOM_CONTACT_WS_ADDRESS]:
    "wecom contact ws自动重连失败:" + WECOM_CONTACT_WS_ADDRESS,
};

const connectFailedClientMap = {
  [ROBOT_WS_ADDRESS]: null,
  [GLOBAL_ADDRESS]: null,
  [WECHAT_ACCOUNT_WS_ADDRESS]: null,
  [WECHAT_CHAT_WS_ADDRESS]: null,
  [WECHAT_CONTACT_WS_ADDRESS]: null,
  [WECOM_ACCOUNT_WS_ADDRESS]: null,
  [WECOM_CHAT_WS_ADDRESS]: null,
  [WECOM_CONTACT_WS_ADDRESS]: null,
} as Record<string, Record<string, any> | null>;

const wsClientsMap = {
  [ROBOT_WS_ADDRESS]: robotWsClient,
  [GLOBAL_ADDRESS]: globalChannelMessageWsClient,
  [WECHAT_ACCOUNT_WS_ADDRESS]: wechatAccountWsClient,
  [WECHAT_CHAT_WS_ADDRESS]: wechatChatWsClient,
  [WECHAT_CONTACT_WS_ADDRESS]: wechatContactWsClient,
  [WECOM_ACCOUNT_WS_ADDRESS]: wecomAccountWsClient,
  [WECOM_CHAT_WS_ADDRESS]: wecomChatWsClient,
  [WECOM_CONTACT_WS_ADDRESS]: wecomContactWsClient,
};

function debounce<T extends (...args: any[]) => any>(func: T, wait: number) {
  let timeout: NodeJS.Timeout | null;
  return function (...args: Parameters<T>) {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => {
      func.apply(null, args);
    }, wait);
  };
}

chatSdkHooks.common.autoReconnectionFailedHook.subscribe((data) => {
  ArmsRum.sendException({
    name: "ws自动重连异常",
    message: autoReconnectionFailMessageMap[data.path],
    properties: {
      error_time: new Date().toLocaleString(),
    },
  });
  if (!connectFailedClientMap[data.path]) {
    connectFailedClientMap[data.path] = data.connectParams || {};
  }
  root.render(
    <Alert
      message={
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          连接尝试重连失败，请检查网络配置或手动重试
          <a
            onClick={debounce(() => {
              console.log(connectFailedClientMap);
              Object.entries(connectFailedClientMap).forEach(
                ([wsAddress, connectParams]) => {
                  if (connectParams) {
                    wsClientsMap[wsAddress].connect(connectParams);
                    connectFailedClientMap[wsAddress] = null;
                  }
                }
              );
              root.render(null);
            }, 300)}
          >
            重试
          </a>
        </div>
      }
      type="error"
      showIcon
      closable
      style={{
        position: "fixed",
        top: 0,
        left: "50%",
        transform: "translate(-50%, 0)",
        width: "50%",
        zIndex: 1000,
      }}
    />
  );
});

const connection =
  (navigator as any).connection ||
  (navigator as any).webkitConnection ||
  (navigator as any).mozConnection;
connection?.addEventListener("change", () => {
  // connection.effectiveType返回的是具体的网络状态：4g/3g/2g
  if (connection.effectiveType === "2g") {
    notification.warning({
      message: "当前网络状态不佳",
      description: "请检查网络连接",
    });
  }
});
